// src/components/ProtocolManagement.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Divider,
  Stack,
  Chip,
  Alert,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

// Your components
import ProtocolList from "./ProtocolList";
import ProtocolForm from "./ProtocolForm";
import UnreviewedPackets from "./UnreviewedPackets";
import ProtocolTester from "./ProtocolTester";
import ProtocolStats from "./ProtocolStats";
import { useLazyGetProtocolQuery } from "../../redux/protocol/protocolApi";

function a11yProps(index) {
  return { id: `proto-tab-${index}`, "aria-controls": `proto-tabpanel-${index}` };
}

export default function ProtocolManagement() {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState("list");
  const [editProtocol, setEditProtocol] = useState(null);
  const [editLoadError, setEditLoadError] = useState("");
  const [loadProtocol, { isFetching: isEditLoading }] = useLazyGetProtocolQuery();

  const tabs = useMemo(
    () => [
      { id: "list", label: "Protocols" },
      { id: "create", label: editProtocol ? "Edit Protocol" : "Create" },
      { id: "unreviewed", label: "Unreviewed Packets" },
      { id: "tester", label: "Tester" },
      { id: "stats", label: "Stats" },
    ],
    [editProtocol]
  );

  const tabIndex = useMemo(() => {
    const idx = tabs.findIndex((t) => t.id === activeTab);
    return idx === -1 ? 0 : idx;
  }, [activeTab, tabs]);

  const handleEdit = async (protocol) => {
    const protocolId = String(protocol?.protocolId || "").trim();
    if (!protocolId) return;

    setEditLoadError("");
    setActiveTab("create");
    try {
      const response = await loadProtocol(protocolId).unwrap();
      setEditProtocol(response?.data || protocol);
    } catch (error) {
      setEditProtocol(protocol);
      setEditLoadError(error?.data?.error || error?.message || "Failed to load protocol details");
    }
  };

  const handleFormSuccess = () => {
    setEditProtocol(null);
    setEditLoadError("");
    setActiveTab("list");
  };

  const handleFormCancel = () => {
    setEditProtocol(null);
    setEditLoadError("");
    setActiveTab("list");
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      {/* Header */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.8, md: 2.2 },
          borderRadius: 3,
          mb: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          borderColor: alpha(theme.palette.primary.main, 0.16),
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, md: 20 } }}>
              Protocol Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              Manage tracker protocols, review unknown packets, and test configurations.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={
                isEditLoading
                  ? "Loading protocol..."
                  : editProtocol
                  ? `Editing: ${editProtocol.protocolId}`
                  : "Ready"
              }
              sx={{ fontWeight: 800, borderRadius: 999 }}
              variant="outlined"
            />
          </Stack>
        </Stack>
      </Paper>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 3 }}>
        <strong>Manual setup flow:</strong> 1. Create and fill values from the tracker manual. 2. Open Tester and paste a real hex packet.
        3. Correct every red error until the tracker ID and fields decode correctly. 4. Only then use a separate Canary port with one test device.
        Current live tracking ports and connected vehicles are not changed by these steps.
      </Alert>

      {/* Tabs */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ px: 1.5, pt: 1.2, bgcolor: "background.paper" }}>
          <Tabs
            value={tabIndex}
            onChange={(_, idx) => {
              const next = tabs[idx]?.id || "list";
              setActiveTab(next);
              if (next !== "create") setEditProtocol(null);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 900,
                minHeight: 44,
              },
            }}
          >
            {tabs.map((t, i) => (
              <Tab key={t.id} label={t.label} {...a11yProps(i)} />
            ))}
          </Tabs>
        </Box>
        <Divider />

        {/* Content */}
        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          {activeTab === "create" && editLoadError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {editLoadError}
            </Alert>
          )}
          {activeTab === "list" && <ProtocolList onEdit={handleEdit} />}
          {activeTab === "create" && (
            <ProtocolForm
              editData={editProtocol}
              isEditLoading={isEditLoading}
              onSuccess={handleFormSuccess}
              onCancel={editProtocol ? handleFormCancel : null}
            />
          )}
          {activeTab === "unreviewed" && <UnreviewedPackets />}
          {activeTab === "tester" && <ProtocolTester />}
          {activeTab === "stats" && <ProtocolStats />}
        </Box>
      </Paper>
    </Box>
  );
}
